import { useTranslation } from '../../i18n/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BrandMark } from '../../components/BrandMark';
import { requestAdSense } from './adsense';
import { canServeAds, type AdPlacement, type AdvertisingConfig } from './config';
import { advertisingConfig } from './repository';
import './advertising.css';

// Keep this query aligned with advertising.css. CSS owns the reserved space.
const wideViewportQuery = '(min-width: 1200px)';

function useWideViewport() {
  const [wide, setWide] = useState(() => window.matchMedia(wideViewportQuery).matches);
  useEffect(() => {
    const media = window.matchMedia(wideViewportQuery);
    const update = () => setWide(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return wide;
}

function SideBanner({ placement, config, wide }: { placement: AdPlacement; config: AdvertisingConfig; wide: boolean }) {
  const tr = useTranslation();
  const adRef = useRef<HTMLModElement>(null);
  const [failed, setFailed] = useState(false);
  const live = canServeAds(config, window.location);
  const positionLabel = placement === 'left' ? 'trái' : 'phải';

  useEffect(() => {
    if (!wide || failed || !canServeAds(config, window.location) || !adRef.current) return;
    let cancelled = false;
    void requestAdSense(adRef.current, config, () => cancelled).catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => { cancelled = true; };
  }, [config, wide, failed]);

  return <aside className={`advertising-rail advertising-rail--${placement}`} aria-label={tr(`Quảng cáo bên ${positionLabel}`)}>
    <span className="advertising-label">{tr("Quảng cáo")}</span>
    <div className="advertising-frame">
      {live ? failed
        ? <div className="advertising-unavailable"><span>{tr("Hiện chưa có quảng cáo.")}</span></div>
        : <ins ref={adRef} className="adsbygoogle" style={{ display: 'inline-block', width: 160, height: 600 }}
          data-ad-client={config.clientId} data-ad-slot={config.slots[placement]} />
        : <div className="advertising-preview">
          <span className="advertising-preview-brand"><BrandMark /> DesignForge</span>
          <span className="advertising-preview-art" aria-hidden="true"><i /><i /><i /></span>
          <strong>{tr("Không gian quảng cáo")}</strong>
          <span className="advertising-preview-position">{tr(placement === 'left' ? 'Banner trái' : 'Banner phải')}</span>
          <span className="advertising-preview-caption">{tr("Bản xem trước")}<span>160 × 600 px</span></span>
        </div>}
    </div>
  </aside>;
}

export function AdvertisingLayout({ children, config = advertisingConfig }: { children: ReactNode; config?: AdvertisingConfig }) {
  const wide = useWideViewport();
  const enabled = config.mode !== 'off';
  return <div className={`advertising-layout${enabled ? ' has-side-ads' : ''}`}>
    {enabled && <SideBanner placement="left" config={config} wide={wide} />}
    {children}
    {enabled && <SideBanner placement="right" config={config} wide={wide} />}
  </div>;
}
